import React, { useEffect, useState } from 'react';
import CustomerSelect from '../../../CustomerSelect/Facelift';
import StatesSelect from '../../../StatesSelect/Facelift';
import { ILongSteelOrdersState } from '../../../../store/ducks/long-steel-orders/types';
import { INewLongSteelOrdersState } from '../../../../store/ducks/new-long-steel-orders/types';
import { TMinimalDescribeICustomer } from '../../../../store/ducks/customers/types';
import { InvoicesState } from '../../../../store/ducks/invoices/types';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import IsManagerCustomerInput from '../../../IsManagerCustomerInput';
import AdminBpInput from '../../../AdminBpInput';
import { uppercaseOnlyFirstLetter } from '../../../../utils/formatStrings';
import { GetFlag } from '../../../../utils/featureFlag';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';

interface ISoldToViewProps {
  orderOrInvoiceState:
    | ILongSteelOrdersState
    | INewLongSteelOrdersState
    | InvoicesState;
  customers: TMinimalDescribeICustomer[];
  isUserAdmin: boolean;
  emitterIds: string[];
  setEmitterIds(emitterIds: string[]): void;

  showCustomerStates: boolean;
  selectedStates: string[];
  onChangeStates(states: string[]): void;
  customersPlaceholder?: string;
  statesPlaceholder?: string;
  onAdminSearch?: (id: string) => void;
  showRichDisplay?: boolean;
}

export const getStatesFromCustomerArray = (
  customers: TMinimalDescribeICustomer[]
): TSelectOptionProps[] => {
  const states: TSelectOptionProps[] = [];
  customers.forEach((customer) => {
    if (!states.find((state) => state.value === customer.locality.state)) {
      states.push({
        label: customer.locality.state,
        value: customer.locality.state
      });
    }
  });

  states.sort((a, b) => {
    if (a.label < b.label) return -1;

    return a.label > b.label ? 1 : 0;
  });

  return states;
};

const getStatesOnlyString = (states: TSelectOptionProps[]): string[] => {
  return states.map((state) => state.value);
};

const SoldToView: React.FC<ISoldToViewProps> = ({
  orderOrInvoiceState,
  customers,
  isUserAdmin,
  showCustomerStates,
  selectedStates,
  onChangeStates,
  emitterIds,
  setEmitterIds,
  statesPlaceholder,
  customersPlaceholder = 'Selecionar emissores',
  onAdminSearch,
  showRichDisplay
}) => {
  const customerState = useSelector(
    (appState: ApplicationState) => appState.customers
  );

  const [states, setStates] = useState<TSelectOptionProps[]>([]);
  const newSearchEmitterInputAdmin = GetFlag('new_search_emitter_input_admin');

  useEffect(() => {
    if (
      showCustomerStates &&
      !customerState.loading &&
      customers.length > 0 &&
      states.length === 0
    ) {
      setStates(getStatesFromCustomerArray(customers));
    }
  }, [showCustomerStates, customerState.loading, customers, states.length]);

  useEffect(() => {
    if (states.length === 1 && selectedStates.length === 0) {
      const [state] = getStatesOnlyString(states);
      onChangeStates([state]);
    }
  });

  useEffect(() => {
    if (states.length === selectedStates.length) {
      setEmitterIds(customers.map((customer) => customer.customerId));
      return;
    }

    const emittersSelected: string[] = customers
      .filter((cst) => selectedStates.some((stt) => stt === cst.locality.state))
      .map((cst) => cst.customerId);

    setEmitterIds(emittersSelected);
  }, [selectedStates]);

  const getSelectedCustomer = (customerId: string) => {
    return customers.find((cust) => cust.customerId === customerId);
  };

  return (
    <>
      {isUserAdmin ? (
        newSearchEmitterInputAdmin ? (
          <AdminBpInput
            title="Emissor do pedido"
            customerId={emitterIds.length > 0 ? emitterIds[0] : ''}
            setCustomerId={(value) => setEmitterIds([value])}
            onChange={(value) => setEmitterIds([value])}
            onSearch={(id) => {
              setEmitterIds([id]);
              onAdminSearch?.(id);
            }}
            loading={orderOrInvoiceState.loading}
            bpError={orderOrInvoiceState.bpError || false}
            customerName={
              !orderOrInvoiceState.loading &&
              customers.length > 0 &&
              emitterIds.length > 0 &&
              emitterIds[0].length > 6
                ? uppercaseOnlyFirstLetter(
                    getSelectedCustomer(emitterIds[0])?.name || '',
                    35
                  )
                : undefined
            }
          />
        ) : (
          <IsManagerCustomerInput
            title="Emissor do pedido"
            customerId={emitterIds.length > 0 ? emitterIds[0] : ''}
            setCustomerId={(value) => setEmitterIds([value])}
            loading={orderOrInvoiceState.loading}
            bpError={orderOrInvoiceState.bpError || false}
            customerName={
              !orderOrInvoiceState.loading &&
              customers.length > 0 &&
              emitterIds.length > 0 &&
              emitterIds[0].length > 6
                ? uppercaseOnlyFirstLetter(
                    getSelectedCustomer(emitterIds[0])?.name || '',
                    35
                  )
                : undefined
            }
          />
        )
      ) : showCustomerStates ? (
        <>
          <StatesSelect
            title={'Estado do emissor'}
            disabled={customerState.loading || orderOrInvoiceState.loading}
            states={states || []}
            selectedStates={selectedStates}
            onChangeState={onChangeStates}
            loading={customerState.loading}
            placeholder={statesPlaceholder}
          ></StatesSelect>
          <CustomerSelect
            title={'Emissor do pedido'}
            placeholder={customersPlaceholder}
            disabled={orderOrInvoiceState.loading || customerState.loading}
            customers={customers.filter((cust) =>
              selectedStates.includes(cust.locality.state)
            )}
            onSelectMultiple={(customerIds) => setEmitterIds(customerIds)}
            selectedCustomerIds={emitterIds}
            isMultipleSelect={true}
            customStyles={{ selectTitleFontSize: '0.8rem' }}
          ></CustomerSelect>
        </>
      ) : (
        <CustomerSelect
          title={'Emissor do pedido'}
          placeholder="Selecionar emissor"
          disabled={orderOrInvoiceState.loading || customerState.loading}
          customers={customers || []}
          onSelect={(customerId) => setEmitterIds([customerId])}
          selectedCustomerId={emitterIds.length ? emitterIds[0] : undefined}
          customStyles={{ selectTitleFontSize: '0.8rem' }}
          showRichDisplay={showRichDisplay}
        ></CustomerSelect>
      )}
    </>
  );
};

export default SoldToView;
